import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Input, InputNumber, Modal, Space, Typography } from 'antd'
import {
  errorNotification,
  postRequest,
  successNotification,
  trackingShipmentActionUrl
} from '../../config'
import {
  buildRefundPayload,
  errorSentence,
  money,
  refundableAmount,
  refundedSoFar,
  validateRefund
} from './utils'

// This dialog moves no money and must never read as though it does. The refund happens by hand in
// Paystack; all that happens here is a row in the action log, so the next person does not refund the
// same customer twice and nobody assumes a colleague already did it.
const RefundModal = ({ visible, shipment, onClose, onDone }) => {
  const row = shipment || {}
  const payment = row.payment || {}
  const refundable = refundableAmount(row)
  const [amount, setAmount] = useState(null)
  const [paystackReference, setPaystackReference] = useState('')
  const [note, setNote] = useState('')
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!visible) return
    setAmount(refundable)
    setPaystackReference('')
    setNote('')
    setErrors([])
    setSaving(false)
    // eslint-disable-next-line
  }, [visible])

  const send = async () => {
    if (saving) return
    setSaving(true)
    const response = await postRequest(
      trackingShipmentActionUrl(row.id, 'actions'),
      buildRefundPayload({ amount, paystackReference, note }, payment.currency || 'GHS'),
      'refund',
      () => {}
    )
    setSaving(false)
    if (!response.success) {
      // The caps are the server's to enforce (409 refund_exceeds_paid, duplicate_refund_reference);
      // its sentence is what the operator needs to read.
      errorNotification(errorSentence(response, 'That refund could not be recorded.'))
      return
    }
    successNotification('Refund recorded.')
    // Recording a refund never ends tracking by itself. The page offers to close it; a person says.
    onDone({ offerClose: !['closed', 'completed'].includes(row.status) })
  }

  const submit = () => {
    if (saving) return
    const found = validateRefund({ amount, paystackReference, note }, refundable)
    setErrors(found)
    if (found.length) return
    Modal.confirm({
      title: `Record a refund of ${money(amount)} for ${row.number}?`,
      content: 'Only confirm once the refund has been made in Paystack.',
      okText: 'Record refund',
      onOk: send
    })
  }

  return (
    <Modal
      visible={visible}
      title="Record a refund"
      onCancel={onClose}
      onOk={submit}
      okText="Record refund"
      okButtonProps={{ disabled: saving }}
      confirmLoading={saving}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="This only records a refund. It does not send money."
          description={
            <span>
              {'Make the refund in the Paystack dashboard first — find the payment by reference '}
              {payment.reference ? (
                <Typography.Text copyable>{payment.reference}</Typography.Text>
              ) : (
                '—'
              )}
              {' — then record it here with Paystack\'s refund reference.'}
            </span>
          }
        />

        <div>
          <div>Amount refunded (GHS)</div>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            value={amount}
            onChange={setAmount}
            style={{ width: '100%' }}
          />
          <div style={{ opacity: 0.65 }}>
            {`Paid ${money(payment.amount, payment.currency)} · already recorded ${money(
              refundedSoFar(row),
              payment.currency
            )}`}
          </div>
        </div>

        <div>
          <div>Paystack refund reference</div>
          <Input
            maxLength={100}
            value={paystackReference}
            onChange={event => setPaystackReference(event.target.value)}
          />
        </div>

        <div>
          <div>Note</div>
          <Input.TextArea
            rows={3}
            maxLength={1000}
            showCount
            value={note}
            onChange={event => setNote(event.target.value)}
          />
        </div>

        {errors.length ? (
          <div>
            {errors.map(message => (
              <div key={message}>
                <Typography.Text type="danger">{message}</Typography.Text>
              </div>
            ))}
          </div>
        ) : null}
      </Space>
    </Modal>
  )
}

RefundModal.propTypes = {
  visible: PropTypes.bool,
  shipment: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func
}

export default RefundModal
